import type { Client } from '@urql/core'
import { graphql } from 'app/generated/gql'
import type { List } from 'app/generated/graphql'
import { runInAction } from 'mobx'
import { openCrossTabWrites } from 'app/lib/crossTabWrites'
import { mergeRecentWrites, type RecentWrite } from 'lib/recentWrites'
import type { CrossTabWrite } from 'app/types/store-writes'
import { EntityStore } from './EntityStore'

// lists returns [List!]! directly, with no pagination wrapper
type ListListResult = { hits: List[]; total: number; page: number }

const LIST_QUERY = graphql(`
  query ListsStore {
    lists(pagination: { limit: 500 }) {
      id
      orgId
      workspaceId
      name
      entityType
      stages {
        id
        name
        color
        system
      }
      defaultStageId
      filters {
        attribute
        secondaryAttribute
        displayName
        operator
        value
        secondaryValue
        static
        uniqOperator
        readOnly
        nonEmpty
        useAsterisk
        group
        subQuery
        type
        defaultValue
        dateOperator
        includeToday
        includeYesterday
        includeWeeks
        limitToMonth
        includeInThePast
        options
      }
      join
      system
      visibility
      createdByUserId
      version
      createdDate
      updatedDate
    }
  }
`)

const GET_QUERY = graphql(`
  query List($id: ID!) {
    list(id: $id) {
      id
      orgId
      workspaceId
      name
      entityType
      stages {
        id
        name
        color
        system
      }
      defaultStageId
      filters {
        attribute
        secondaryAttribute
        displayName
        operator
        value
        secondaryValue
        static
        uniqOperator
        readOnly
        nonEmpty
        useAsterisk
        group
        subQuery
        type
        defaultValue
        dateOperator
        includeToday
        includeYesterday
        includeWeeks
        limitToMonth
        includeInThePast
        options
      }
      join
      system
      visibility
      createdByUserId
      version
      createdDate
      updatedDate
    }
  }
`)

const CREATE_MUTATION = graphql(`
  mutation CreateList($input: CreateListInput!) {
    createList(input: $input) {
      id
      orgId
      workspaceId
      name
      entityType
      stages {
        id
        name
        color
        system
      }
      defaultStageId
      filters {
        attribute
        secondaryAttribute
        displayName
        operator
        value
        secondaryValue
        static
        uniqOperator
        readOnly
        nonEmpty
        useAsterisk
        group
        subQuery
        type
        defaultValue
        dateOperator
        includeToday
        includeYesterday
        includeWeeks
        limitToMonth
        includeInThePast
        options
      }
      join
      system
      visibility
      createdByUserId
      version
      createdDate
      updatedDate
    }
  }
`)

const UPDATE_MUTATION = graphql(`
  mutation UpdateList($id: ID!, $input: UpdateListInput!) {
    updateList(id: $id, input: $input) {
      id
      orgId
      workspaceId
      name
      entityType
      stages {
        id
        name
        color
        system
      }
      defaultStageId
      filters {
        attribute
        secondaryAttribute
        displayName
        operator
        value
        secondaryValue
        static
        uniqOperator
        readOnly
        nonEmpty
        useAsterisk
        group
        subQuery
        type
        defaultValue
        dateOperator
        includeToday
        includeYesterday
        includeWeeks
        limitToMonth
        includeInThePast
        options
      }
      join
      system
      visibility
      createdByUserId
      version
      createdDate
      updatedDate
    }
  }
`)

const DELETE_MUTATION = graphql(`
  mutation DeleteList($id: ID!) {
    deleteList(id: $id)
  }
`)

type ListEntityStore = EntityStore<List, ListListResult, typeof CREATE_MUTATION, typeof UPDATE_MUTATION>

export class ListStore extends EntityStore<List, ListListResult, typeof CREATE_MUTATION, typeof UPDATE_MUTATION> {
  /**
   * The `lists` query reads from a search index that lags writes by a second or two, so
   * a reload right after a write returns the old state: a created list is missing, a
   * rename reverts, a deleted list comes back. The base class already applies each
   * mutation response to `list.hits`; these records let a reload keep that result for
   * ten seconds instead of replacing it with the lagging index.
   */
  private recentWrites = new Map<string, RecentWrite<List>>()
  private shareWrite = openCrossTabWrites<List>(`lists`, (write) => this.applyOtherTabWrite(write))

  constructor(client: Client) {
    super(client, {
      listQuery: LIST_QUERY,
      getQuery: GET_QUERY,
      createMutation: CREATE_MUTATION,
      updateMutation: UPDATE_MUTATION,
      deleteMutation: DELETE_MUTATION,
      typename: `List`,
      extractList: (data: any) => ({ hits: data.lists, total: data.lists.length, page: 0 }),
      extractOne: (data: any) => data.list,
      extractMutated: (data: any) => data.updateList ?? data.createList,
    })
  }

  async create(input: Parameters<ListEntityStore[`create`]>[0]) {
    const result = await super.create(input)
    const created = !result.error && result.data ? this.config.extractMutated?.(result.data) : undefined
    if (created) {
      this.recentWrites.set(created.id, { row: created, at: Date.now() })
      this.shareWrite({ kind: `upsert`, record: created })
    }
    return result
  }

  async update(id: string, input: Parameters<ListEntityStore[`update`]>[1]) {
    const result = await super.update(id, input)
    const updated = !result.error && result.data ? this.config.extractMutated?.(result.data) : undefined
    if (updated) {
      this.recentWrites.set(id, { row: updated, at: Date.now() })
      this.shareWrite({ kind: `upsert`, record: updated })
    }
    return result
  }

  async remove(id: string) {
    const result = await super.remove(id)
    if (!result.error) {
      this.recentWrites.set(id, { row: null, at: Date.now() })
      this.shareWrite({ kind: `delete`, id })
    }
    return result
  }

  protected overlayList() {
    if (!this.list) return
    const hits = mergeRecentWrites(this.list.hits, this.recentWrites, (list) => list)
    if (hits) this.list = { ...this.list, hits, total: hits.length }
  }

  /** Applies another tab's write the way this tab's own lands, including over the next reloads. */
  private applyOtherTabWrite(write: CrossTabWrite<List>) {
    const id = write.kind === `delete` ? write.id : write.record.id
    const list = write.kind === `delete` ? null : write.record
    this.recentWrites.set(id, { row: list, at: Date.now() })
    if (!this.list) return
    const hits = this.list.hits.filter((item) => item.id !== id)
    const index = this.list.hits.findIndex((item) => item.id === id)
    if (list) hits.splice(index === -1 ? 0 : index, 0, list)
    runInAction(() => {
      this.list = { ...this.list!, hits, total: hits.length }
    })
  }
}
